<template>
  <TooltipProvider :delay-duration="1000" ignore-non-keyboard-focus>
    <div style="height: 100vh; width: 100vw">
      <DraftPage
        v-if="newsletter"
        v-model="body"
        v-model:template="template"
        :title="title || ''"
        playground
        :save="handleSave"
        :newsletter="newsletter"
        :preview-config="{ type: 'email_draft' }"
        :breadcrumbs="[{ title: `Playground` }]"
        @update:title="title = $event || ''"
      />
    </div>
  </TooltipProvider>
</template>

<script setup lang="ts">
import { TooltipProvider } from "reka-ui";
import * as v from "valibot";
import { onMounted, ref, watch } from "vue";

import DraftPage from "@/components/setting/DraftPage.vue";
import { useNewsletter } from "@/store/stores/account";
import type { NewsletterEmailTemplate } from "@/types/email";

import { samplePlaygroundEmail } from "./sample";

const persistSchema = v.object({
  title: v.string(),
  content: v.string(),
});

type PersistData = v.InferOutput<typeof persistSchema>;

const newsletter = useNewsletter();

// URL compression utilities using browser-native compression
const compressToBase64 = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);

  if ("CompressionStream" in window) {
    try {
      const stream = new CompressionStream("gzip");
      const writer = stream.writable.getWriter();
      const reader = stream.readable.getReader();

      writer.write(data);
      writer.close();

      const chunks: Uint8Array[] = [];
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) chunks.push(value);
      }

      const compressed = new Uint8Array(
        chunks.reduce((acc, chunk) => acc + chunk.length, 0)
      );
      let offset = 0;
      for (const chunk of chunks) {
        compressed.set(chunk, offset);
        offset += chunk.length;
      }

      return btoa(String.fromCharCode(...compressed));
    } catch (e) {
      console.warn("Compression failed, using uncompressed:", e);
    }
  }

  // Fallback to base64 encoding without compression
  return btoa(encodeURIComponent(text));
};

const decompressFromBase64 = async (compressed: string): Promise<string> => {
  try {
    if ("DecompressionStream" in window) {
      try {
        const binaryString = atob(compressed);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const stream = new DecompressionStream("gzip");
        const writer = stream.writable.getWriter();
        const reader = stream.readable.getReader();

        writer.write(bytes);
        writer.close();

        const chunks: Uint8Array[] = [];
        let done = false;

        while (!done) {
          const { value, done: readerDone } = await reader.read();
          done = readerDone;
          if (value) chunks.push(value);
        }

        const decompressed = new Uint8Array(
          chunks.reduce((acc, chunk) => acc + chunk.length, 0)
        );
        let offset = 0;
        for (const chunk of chunks) {
          decompressed.set(chunk, offset);
          offset += chunk.length;
        }

        const decoder = new TextDecoder();
        return decoder.decode(decompressed);
      } catch (e) {
        console.warn("Decompression failed, trying fallback:", e);
      }
    }

    // Fallback: assume it's just base64 encoded
    return decodeURIComponent(atob(compressed));
  } catch (e) {
    console.warn("Failed to decode base64:", e);
    return "";
  }
};

// Initialize content from URL parameters or defaults
const urlParams = new URLSearchParams(window.location.search);

const title = ref(samplePlaygroundEmail.subject);
const body = ref(samplePlaygroundEmail.body);
const template = ref<NewsletterEmailTemplate | null | undefined>(undefined);

// Load content from URL on mount
onMounted(async () => {
  // Check for compressed content first
  const compressedData = urlParams.get("c");
  if (compressedData) {
    try {
      const raw = await decompressFromBase64(compressedData);
      const json = JSON.parse(raw);
      const data = v.parse(persistSchema, json);

      title.value = data.title;
      body.value = data.content;
    } catch (e) {
      console.warn("Failed to decompress URL data:", e);
    }
  }
});

// Update URL when content changes
const updateURL = async () => {
  const url = new URL(window.location.href);

  // Clear old parameters
  url.searchParams.delete("c");

  const data: PersistData = {
    title: title.value,
    content: body.value,
  };

  // Only add to URL if there's actual content
  if (data.title || data.content) {
    const compressed = await compressToBase64(JSON.stringify(data));
    url.searchParams.set("c", compressed);
  }

  window.history.replaceState({}, "", url.toString());
};

// Watch for changes and update URL (debounced)
let updateTimeout: ReturnType<typeof setTimeout>;
watch([title, body], () => {
  clearTimeout(updateTimeout);
  updateTimeout = setTimeout(() => {
    updateURL();
  }, 1000); // Debounce to avoid too many URL updates
});

const handleSave = async () => {
  return;
};

// Listen for messages from the parent window
window.addEventListener("message", (event) => {
  if (event.data.type === "SET_EDITOR_CONTENT") {
    body.value = event.data.content;
  }
});
</script>
